import { orderBy } from "firebase/firestore";
import { useEffect, useState } from "react";
import {
    Alert,
    Button,
    ButtonGroup,
    Card,
    Dropdown,
    Form,
    Modal,
} from "react-bootstrap";
import {
    FaCog,
    FaExclamationTriangle,
    FaPlus,
    FaTrash,
    FaTrashRestore,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import {
    useLocationCollection,
    useOrganizationDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import {
    CorvusList,
    CorvusListItem,
    CorvusListItemText,
} from "../../components/CorvusList";
import Layout, { Title } from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";

export default function LocationListPage() {
    const { organizationId } = useParams();
    const { getUserIsAdmin, userClaims } = useUser();
    const [locations] = useLocationCollection(organizationId, [
        orderBy("name", "asc"),
    ]);
    const navigate = useNavigate();
    const [organization] = useOrganizationDocument(organizationId);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [showDeletedLocations, setShowDeletedLocations] = useState(false);

    useEffect(() => {
        if (!organizationId || organizationId === "undefined") {
            navigate(urls.organization(userClaims.organizationId), {
                replace: true,
            });
        }
    }, [organizationId, userClaims, navigate]);

    const hideDeleteModal = () => setDeleteModalVisible(false);
    const showDeleteModal = () => setDeleteModalVisible(true);

    return (
        <Layout>
            <title>Locations | MyCorvus</title>

            {getUserIsAdmin() && (
                <>
                    <Breadcrumbs
                        path={breadcrumbPaths.organization}
                        organization={organization}
                    />

                    <Modal show={deleteModalVisible} onHide={hideDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete organization</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Delete organization <b>{organization?.name}</b>?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="outline-secondary"
                                onClick={hideDeleteModal}
                            >
                                Cancel
                            </Button>
                            <Dropdown as={ButtonGroup} align={{ lg: "start" }}>
                                <Button
                                    variant="danger"
                                    onClick={() =>
                                        api
                                            .deleteOrganization(organizationId)
                                            .then(() =>
                                                navigate(
                                                    urls.allOrganizations(),
                                                ),
                                            )
                                    }
                                >
                                    <FaTrash
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    Delete
                                </Button>
                            </Dropdown>
                        </Modal.Footer>
                    </Modal>
                </>
            )}

            <Card>
                <Title
                    title="Locations"
                    backButtonUrl={
                        getUserIsAdmin() ? urls.organizations() : undefined
                    }
                >
                    {getUserIsAdmin() && (
                        <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
                            <Button
                                variant="outline-danger"
                                onClick={showDeleteModal}
                            >
                                <FaTrash className="me-2" />
                                Delete organization
                            </Button>

                            <Button
                                variant="outline-success"
                                as={Link}
                                to={urls.locationCreate(organizationId)}
                            >
                                <FaPlus className="me-2" />
                                New Location
                            </Button>

                            <Button
                                variant="outline-secondary"
                                as={Link}
                                to={urls.organizationSettingsPage(
                                    organizationId,
                                )}
                            >
                                <FaCog className="me-2" />
                                Settings
                            </Button>
                        </div>
                    )}
                </Title>

                {getUserIsAdmin() && organization?.deleted && (
                    <Card.Body className="pb-4 pt-0 px-4">
                        <Alert variant="danger" className="m-0 py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>
                                    <FaExclamationTriangle
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    This organization is deleted!
                                </span>

                                <Button
                                    variant="primary"
                                    onClick={() =>
                                        api.restoreOrganization(organizationId)
                                    }
                                >
                                    <FaTrashRestore className="me-2" />
                                    Restore
                                </Button>
                            </div>
                        </Alert>
                    </Card.Body>
                )}

                {getUserIsAdmin() && (
                    <Form.Check
                        type="switch"
                        id="show-deleted"
                        label="Show deleted locations"
                        checked={showDeletedLocations}
                        onChange={(e) =>
                            setShowDeletedLocations(e.target.checked)
                        }
                        className="ms-4"
                    />
                )}

                <CorvusList>
                    {locations
                        ?.filter((location) =>
                            showDeletedLocations
                                ? location.deleted
                                : !location.deleted,
                        )
                        .map((location) => (
                            <CorvusListItem
                                className="p-0"
                                key={location.id}
                                as={Link}
                                to={urls.location(organizationId, location.id)}
                            >
                                <CorvusListItemText>
                                    {location.name}
                                </CorvusListItemText>
                            </CorvusListItem>
                        ))}
                </CorvusList>
            </Card>
        </Layout>
    );
}
